import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux, useDispatch } from 'react-redux'
import { parse as parseUrl, format as formatUrl } from 'url'
import _get from 'lodash/get'
import {
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_EPISODE,
  SUBTYPE_CONTENT_GUIDE,
  SUBTYPE_CONTENT_EVENT,
} from '../../../services/content-type/index'
import {
  isSubtypeEvent,
  isGuide,
  isEpisode,
  isVideo,
  isSeries,
  getContentTags,
  getFeaturedContent,
  getFeatureMedia,
  isSeriesNoEpisodes,
  isGuideNotStartedYet,
  isGaiaPlusFeature,
  featureAllowedWithUserSubscription,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_4K_NO_TEXT,
  CONTENT_IMAGE_TYPE_LOGO,
} from '../../../services/content/index'
import { DETAIL_PAGE_STATIC_KEY } from '../../../services/detail/index'
import { hasLiveAccessEntitlement, isEntitled } from '../../../services/subscription/index'
import { CLICK_TO_DETAILS_PAGE_GA4_EVENT, PREVIEW_PLAYED_GA4_EVENT } from '../../../services/event-tracking/index'
import { stringify as stringifyQuery } from '../../../services/query-string/index'
import { formatDuration, truncate } from '../../../theme/web-app/index'
import { getPrimary } from '../../../services/languages/index'
import { URL_CHECKOUT, URL_GAIA_PLUS } from '../../../services/url/constants'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../../Picture/index'
import { H1, H2, HEADING_TYPES } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import ButtonAdmin, { BUTTON_ADMIN_ALIGN_RIGHT_BOTTOM } from '../../ButtonAdmin/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../../ButtonSignUp/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import Link from '../../Link/index'
import TextSeasonEpisode from '../../TextSeasonEpisode/index'
import LearnerLevelLabel from '../../LearnerLevelLabel/index'
import WatchV2 from '../../Watch.v2/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { get as getConfig } from '../../../config/index'
import { shouldShowWinbackCheckout, WINBACK_FLAG, WINBACK_VARIATION } from '../../../services/winback/index'
import { setEventDefault } from '../../../services/event-tracking/actions'
import { updateOptimizelyFeatureUser } from '../../../services/optimizely-feature/actions'
import { EN } from '../../../services/languages/constants'
import OptimizelyFeature from '../../OptimizelyFeature/index'
import {
  getData,
  getNodeType,
  getOptimizedImages,
  isGaiaOriginal,
  isYogaOrMeditation,
  getDetailPageUiLocationGQL,
  getJumbotronWatchEventLabelGQL,
  getJumbotronWatchTextLabel,
  onClickDetailPageTag,
  buildJumbotronUpstreamContextGQL,
  shouldShowWinbackButtons,
  handleSignUpClicked,
} from '../utils'

// Guide Policy Gaia Plus Upgrade Toggle
const config = getConfig()
const guidePolicyGaiaPlusUpgrade = !!_get(config, ['features', 'guidePolicyGaiaPlusUpgrade'])

/**
 * Background for the Jumbotron with image optimization
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data for the episode/video
 * @returns {JSX.Element} Jumbotron Optimized Background
 */
function JumbotronBackground (props) {
  const { detailData = Map() } = props
  const standardImage = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_KEYART_NO_TEXT)
  const image4k = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_KEYART_4K_NO_TEXT)
  // provide image fallback logic
  const image = image4k || standardImage || Map()
  const imageUrls = getOptimizedImages({ url: image.get('url') }) || {}
  const {
    fallbackImageUrl,
    smallImageUrl,
    mediumSmallImageUrl,
    mediumImageUrl,
    largeImageUrl,
    xLargeImageUrl,
  } = imageUrls

  return (
    <div className="detail-jumbotron__background-optimized">
      <div className="detail-jumbotron__background-optimized-wrapper">
        <div className="detail-jumbotron__blackout" />
        <div className="detail-jumbotron__image-wrapper">
          <div className="detail-jumbotron__background-optimized-gradient" />
          <Picture
            fallbackSrc={fallbackImageUrl}
            width={'608'}
            height={'342'}
            alt=""
            imageClassName="detail-jumbotron__optimized-image"
            loading={IMAGE_LOADING_LAZY}
            sources={[
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: smallImageUrl,
                media: '(max-width: 480px)',
              },
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: mediumSmallImageUrl,
                media: '(max-width: 608px)',
              },
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: mediumImageUrl,
                media: '(max-width: 768px)',
              },
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: largeImageUrl,
                media: '(max-width: 1199px)',
              },
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: xLargeImageUrl,
                media: '(min-width: 1200px)',
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Tag for Guide Flair
 * @param {Object} props - The props
 * @param {Map} props.staticText - Static Text
 * @param {Boolean} props.isEvent - if this is an event detail page
 * @param {Boolean} props.isGuideDetailPage - if this is a guide detail page
 * @returns {JSX.Element} Gaia Original
 */
function GuideEventFlair (props) {
  const {
    staticText = Map(),
    isEvent = false,
    isGuideDetailPage,
  } = props
  const flairText = isEvent ? staticText.get('eventFlair') : staticText.get('guideFlair')

  if (isEvent || isGuideDetailPage) {
    return (
      <div className="detail-jumbotron__guide-event-flair">
        {flairText}
      </div>
    )
  }

  return null
}

/**
 * Tag for Gaia Original series
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @returns {JSX.Element|null} Gaia Original label
 */
function GaiaOriginal (props) {
  const { detailData } = props
  const detailStudio = detailData.get('studio', '')
  const seriesStudio = detailData.get('seriesStudio', '')
  const studio = seriesStudio || detailStudio
  const gaiaOriginal = isGaiaOriginal({ studio })
  const yogaOrMeditation = isYogaOrMeditation(detailData.get('contentSubtype'))

  if (yogaOrMeditation) {
    return null
  }

  return gaiaOriginal
    ? <div className="detail-jumbotron__gaia-original" />
    : null
}

/**
 * @function SeriesLogoOrTitle
 * Renders a series logo or title based on the provided props.
 *
 * @param {Object} props - The component's properties.
 * @param {Map} props.detailData - Data about the series or episode.
 * @param {Boolean} props.isSeriesDetailPage - Indicates if it is the detail page for a series.
 * @param {Boolean} props.isEpisodeDetailPage - Indicates if it is the detail page for an episode.
 * @param {Boolean} props.isSeriesDetailPageNoEpisodes - Indicates if the series detail page
 *  has no episodes.
 * @param {Boolean} props.isGuideAndNotStarted - Indicates if the content is in a guide
 *  and not started yet.
 * @returns {JSX.Element|null} - Returns a JSX element representing the series logo or title,
 * or null if conditions are not met.
 */
function SeriesLogoOrTitle (props) {
  const {
    detailData,
    isSeriesDetailPage,
    isEpisodeDetailPage,
    isSeriesDetailPageNoEpisodes,
    isGuideAndNotStarted,
    detailLocationType,
  } = props

  if (!isSeriesDetailPage && !isEpisodeDetailPage) {
    return null
  }

  const seriesTitle = detailData.get('seriesTitle', '') || detailData.get('title', '')
  const seriesUrl = detailData.get('seriesPath', '') || detailData.get('path', '')
  const detailLogo = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_LOGO) || Map()
  const seriesLogo = detailData.get('seriesImages', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_LOGO) || Map()
  const logo = detailLogo.get('url') || seriesLogo.get('url')

  const ga4EventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT
    .mergeDeep({
      content_id: detailData.get('contentId'),
      content_name: detailData.get('title'),
      content_type: detailData.get('contentType'),
      parent_series_id: detailData.get('seriesId'),
      parent_series_name: seriesTitle,
      ui_location: getDetailPageUiLocationGQL(detailLocationType),
      ui_element: 'Detail Page Primary',
    })

  if (logo) {
    return (
      <div className="detail-jumbotron__logo">
        <Link
          to={seriesUrl}
          className="detail-jumbotron__title-heading-link"
          ga4EventData={ga4EventData}
        >
          <img
            src={logo}
            alt={seriesTitle}
          />
        </Link>
      </div>
    )
  }

  return (
    <div className={`detail-jumbotron__title${isSeriesDetailPageNoEpisodes ? ' detail-jumbotron__title--no-episodes' : ''}`}>
      <H1 as={HEADING_TYPES.H6} className={`detail-jumbotron__title-heading${isSeriesDetailPageNoEpisodes || isGuideAndNotStarted ? ' detail-jumbotron__title-heading--no-episodes' : ''}`}>
        <Link
          to={seriesUrl}
          className={`detail-jumbotron__title-heading-link${isSeriesDetailPageNoEpisodes ? ' detail-jumbotron__title-heading-link--no-episodes' : ''}`}
          ga4EventData={ga4EventData}
        >
          {seriesTitle}
        </Link>
      </H1>
    </div>
  )
}

/**
 * Watch Label for the Jumbotron
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isEvent - Is series event
 * @param {Boolean} props.hasLiveAccess - Has use access to watch Event+ video
 * @param {Boolean} props.isVideoDetailPage - If this is a video detail page
 * @param {Boolean} props.isSeriesDetailPage - If this is a series detail page
 *  with the user's subscription
 * @returns {JSX.Element|null} Watch Label / null
 */
function WatchLabel (props) {
  const {
    detailData,
    isEvent,
    hasLiveAccess,
    staticText = Map(),
    isVideoDetailPage,
    isSeriesDetailPage,
    isEpisodeDetailPage,
  } = props
  const featuredContent = getFeaturedContent({ data: detailData })
  const contentType = detailData.get('contentType')
  const tileType = isEpisodeDetailPage ? detailData.get('seriesFeaturedType') : detailData.getIn(['featuredContent', 'featuredType'])
  const nodeType = getNodeType({ data: detailData })
  const isNew = detailData.get('isNew')
  const data = isSeriesDetailPage ? featuredContent : detailData
  const featureAllowed = featureAllowedWithUserSubscription({
    data,
    contentType: isSeriesDetailPage ? featuredContent.get('contentType') : contentType,
  }) || data.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'isFreeAllowed'])
  const watchAccess = !!data.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'allowed'])
  const hasWatchAccess = hasLiveAccess || watchAccess
  const watchLabel = isEvent && !hasWatchAccess && !featureAllowed
    ? getJumbotronWatchEventLabelGQL(detailData, staticText)
    : getJumbotronWatchTextLabel(tileType, isNew, nodeType, staticText)

  // if an episode or series, output the label
  // standalone videos get no label
  if (!isVideoDetailPage && watchLabel) {
    return (
      <div className="detail-jumbotron__watch-label">
        <div className="detail-jumbotron__watch-label-bar" />
        <span>{watchLabel}</span>
      </div>
    )
  }

  return null
}

/**
 * Featured Guide details (featuring, dates, duration)
 * @param {Object} props - The props
 * @param {Map} props.detailData - The episode/video content data
 * @param {Map} props.staticText - The static text
 * @param {Map} props.isGuideAndNotStarted - If this is a guide page and the user has not started it
 * @param {Boolean} props.isEpisodeDetailPage - If this is an episode page
 * @param {Boolean} props.isSeriesDetailPage - If this is a series page
 * @returns {JSX.Element} Featured Details for Guides
 */
function FeaturedGuideDetails (props) {
  const {
    detailData,
    staticText = Map(),
    isGuideAndNotStarted,
    isEpisodeDetailPage,
    isSeriesDetailPage,
  } = props
  const featuredContent = getFeaturedContent({ data: detailData })
  const totalEpisodes = detailData.get('seriesEpisodesCount') || detailData.get('episodesCount')
  const episodeNumber = featuredContent.get('episodeNumber') ? featuredContent.get('episodeNumber') : detailData.get('episodeNumber')
  let guest

  if (isSeriesDetailPage) {
    guest = featuredContent.get('cast')
  }

  if (isEpisodeDetailPage) {
    guest = detailData.get('cast')
  }

  return (
    <div className="detail-jumbotron__featured-guide-details">
      {guest && (
        <div className="detail-jumbotron__featured-guide-guest">
          <span className="detail-jumbotron__featured-guest--highlighted">{staticText.get('featuring')}:</span> {guest}
        </div>
      )}
      {!!totalEpisodes && (
        <div className="detail-jumbotron__featured-guide-count">
          {isGuideAndNotStarted
            ? `${totalEpisodes} ${staticText.get('lessons')}`
            : `${staticText.get('lesson')} ${episodeNumber}`
          }
        </div>
      )}
    </div>
  )
}

/**
 * Featured details (episode-season, duration, guest/host)
 * @param {Object} props - The props
 * @param {Map} props.nodeData - The episode/video content data
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isSeriesDetailPage - If the detail page is of contentType series
 * @returns {JSX.Element} Featured Details
 */
function FeaturedDetails (props) {
  const {
    detailData,
    staticText = Map(),
    isSeriesDetailPage,
  } = props
  const contentType = detailData.get('contentType')
  const featureMedia = getFeatureMedia({ contentType, data: detailData })
  const featuredContent = getFeaturedContent({ data: detailData })
  const nodeData = isSeriesDetailPage ? featuredContent : detailData
  const parentContentSubtype = detailData.get('seriesContentSubtype') || detailData.get('contentSubtype')
  const duration = featureMedia.get('duration', 0)
  const season = nodeData.get('seasonNumber')
  const episode = nodeData.get('episodeNumber')
  const host = nodeData.get('instructor')
  const guest = nodeData.get('cast')
  const hostLabel = isYogaOrMeditation(nodeData.get('contentSubtype')) || isYogaOrMeditation(parentContentSubtype)
    ? staticText.get('teacher')
    : staticText.get('host')
  const learnerLevelLabel = Map.isMap(detailData) ? detailData.get('learnerLevelLabel') : null

  return (
    <div className="detail-jumbotron__featured-details">
      {learnerLevelLabel ? (
        <LearnerLevelLabel learnerLevelLabel={learnerLevelLabel} />
      ) : null}
      {season && episode && (
        <TextSeasonEpisode
          className={['detail-jumbotron__featured-season']}
          season={season}
          episode={episode}
        />
      )}
      {duration ? (
        <div className="detail-jumbotron__featured-duration">
          {formatDuration(duration)}
        </div>
      ) : null}
      {guest && (
        <div className="detail-jumbotron__featured-guest">
          {staticText.getIn(['featuring'])}: {guest}
        </div>
      )}
      {!guest && host && (
        <div className="detail-jumbotron__featured-instructor">
          {hostLabel}: {host}
        </div>
      )}
    </div>
  )
}

/**
 * Tags for the detail page
 * @param {Object} props - The props
 * @param {Map} props.detailData - The series/episode content data
 * @param {String} props.userLanguage - The user's language setting
 * @param {Boolean} props.isSeriesDetailPage - Whether or not that this piece of content is a series
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Tags for the detail page
 */
function Tags (props) {
  const {
    detailData = Map(),
    staticText,
    userLanguage,
    isSeriesDetailPage,
    detailLocationType,
  } = props
  const featuredContent = getFeaturedContent({ data: detailData })
  let data = detailData || Map()

  if (isSeriesDetailPage) {
    data = detailData || featuredContent || Map()
  }

  const id = data.get('contentId')
  const url = data.get('path', '')
  const title = data.get('title', '')
  const references = List.isList(data.get('references')) ? data.get('references', List()) : List()
  const contentSubtype = data.get('contentSubtype')
  const contentType = data.get('contentType')
  const displayType = data.get('displayType')
  const isNew = data.get('isNew', null)
  const hasNewEpisodes = data.get('hasNewEpisodes', false)
  const tags = getContentTags({
    tags: references,
    url,
    contentSubtype,
    isNew,
    hasNewEpisodes,
    contentType,
    staticText,
    userLanguage,
    displayType,
  }) || List()
  const dispatch = useDispatch()
  const uiLocation = getDetailPageUiLocationGQL(detailLocationType)

  if (tags.size > 0) {
    return (
      <div className="detail-jumbotron__tags">
        {
          tags.map((t) => {
            if (!Map.isMap(t)) {
              return null
            }

            const type = t.get('type')

            return (
              <React.Fragment key={`${t}-${id}`}>
                {
                  t.get('path')
                    ? <Link
                        to={t.get('path')}
                        className={`detail-jumbotron__tags-item detail-jumbotron__tags-item--link${type ? ` detail-jumbotron__tags-item--${type}` : ''}`}
                        onClick={() => onClickDetailPageTag({
                          tagId: t.get('contentId'),
                          tagName: t.get('title') || t.get('name'),
                          id,
                          contentType,
                          title,
                          dispatch,
                          uiLocation,
                          data,
                        })}
                      >
                      {t.get('title') || t.get('name')}
                    </Link>
                    : <span className={`detail-jumbotron__tags-item${type ? ` detail-jumbotron__tags-item--${type}` : ''}`}>
                        {t.get('title') || t.get('name')}
                      </span>
                }
              </React.Fragment>
            )
          })
        }
      </div>
    )
  }

  return null
}

/**
 * Winback Flow Buttons for users who have lost access
 * NOTE: move to DetailAccessButtons once the feature guidePolicyGaiaPlusUpgrade is enabled
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Winback buttons (subscribe + watch trailer)
 */
function DetailWinbackButtons (props) {
  const {
    detailData,
    staticText,
  } = props
  const dispatch = useDispatch()
  const previewMediaId = detailData.getIn(['featuredContent', 'previewMedia', 'mediaId'], null)
    || detailData.getIn(['previewMedia', 'mediaId'], null)
  const previewUrl = previewMediaId ? detailData.getIn(['featuredContent', 'path']) || detailData.getIn(['path'], null) : null

  const handlePreviewClicked = () => {
    dispatch(
      setEventDefault(PREVIEW_PLAYED_GA4_EVENT.mergeDeep({
        content_id: detailData.get('contentId', null),
        content_type: detailData.get('contentType', null),
        content_name: detailData.get('title', null),
        media_id: previewMediaId,
        media_language: detailData.get('languageFullText', EN),
        parent_series_name: detailData.get('seriesTitle', null),
        parent_series_id: detailData.get('seriesId', null),
        is_featured_media: false,
        ui_element: 'Jumbotron Detail Page',
      })),
    )
  }

  return (
    <div className="detail-jumbotron__featured-winback-buttons">
      <Button
        className="detail-jumbotron__featured-winback-buttons-subscribe"
        url={URL_CHECKOUT}
        type={TYPES.PRIMARY}
        onClick={() => handleSignUpClicked(props)}
      >
        {staticText.getIn(['subscribe'])}
      </Button>
      {
        previewUrl
          ? <Button
            type={TYPES.SECONDARY}
            icon={ICON_TYPES.PLAY}
            url={previewUrl}
            query={{ fullplayer: 'preview' }}
            className="detail-jumbotron__featured-winback-buttons-watch-trailer"
            onClick={handlePreviewClicked}
          >
            <IconV2 className="detail-jumbotron__featured-winback-buttons-watch-trailer-icon" type={ICON_TYPES.PLAY} />
            {staticText.get('watchTrailer')}
          </Button>
          : null
      }
    </div>
  )
}

/**
 * Label for a tile (Gaia original, etc.)
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Boolean} props.isSeriesDetailPage - Is the current page a series page
 * @param {Boolean} props.isEpisodeDetailPage - Is the current page an episode page
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isEvent - Is series event
 * @param {Boolean} props.isGuideDetailPage - is a Guide page
 * @param {Boolean} props.hasLiveAccess - If the user has a Gaia+ subscription
 * @param {Boolean} props.featureIsGaiaPlus - If the feature media is Gaia+ only
 * @returns {JSX.Element} Watch button
 */
function WatchNow (props) {
  const {
    detailData,
    isSeriesDetailPage,
    staticText,
    isEvent,
    isGuideDetailPage,
    hasLiveAccess,
    featureIsGaiaPlus,
    detailLocationType,
    isLoggedIn,
    auth,
    onWatchNowClick,
  } = props
  const contentType = detailData.get('contentType')
  const featuredContent = getFeaturedContent({ data: detailData })
  const nodeData = isSeriesDetailPage ? featuredContent : detailData
  const nodeType = getNodeType({ data: detailData })

  const jumbotronUpstreamContext = buildJumbotronUpstreamContextGQL({
    auth,
    nodeData,
    parentNodeData: detailData,
    nodeType,
    detailLocationType,
  })
  const isGuideOrEvent = isEvent || isGuideDetailPage
  const feature = getFeatureMedia({ data: detailData, contentType })
  const watchPath = isSeriesDetailPage ? featuredContent.get('path', '') : detailData.get('path', '')
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])
  const isFeatureAllowed = featureAllowedWithUserSubscription({ data: nodeData })
  const isFeatureAvailableToUser = isFreeAllowed || watchAccess || isFeatureAllowed
  const isCountryAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'], true)

  if (Map.isMap(feature) && feature.size === 0) {
    return null
  }

  if (!isCountryAllowed && !watchAccess) {
    return (
      <div className="detail-jumbotron__featured-action-buttons">
        <div className="detail-jumbotron__featured-unavailable-text">
          {staticText.get('notAvailableInArea')}
        </div>
      </div>
    )
  }

  if (isGuideOrEvent || featureIsGaiaPlus) {
    return (
      <div className="detail-jumbotron__featured-action-buttons">
      {isFeatureAvailableToUser && <WatchV2
        upstreamContext={jumbotronUpstreamContext}
        className={hasLiveAccess
          ? 'detail-jumbotron__featured-button detail-jumbotron__featured-watch-now'
          : 'detail-jumbotron__featured-button-watch'
        }
        text={staticText.get('watchNow')}
        url={watchPath}
        watchAccess={watchAccess}
        isFree={isFreeAllowed}
        iconType={ICON_TYPES.PLAY}
        buttonType={TYPES.ICON_PRIMARY}
        onClickWatch={onWatchNowClick}
      />}
      {!hasLiveAccess && !watchAccess && <Button
        className="detail-jumbotron__featured-button-upgrade"
        url={URL_GAIA_PLUS}
        type={TYPES.LABELED}
        label={staticText.getIn(['watchFullSeries'])}>
        {isLoggedIn ? staticText.getIn(['upgrade']) : staticText.getIn(['signUp'])}
      </Button>}
    </div>
    )
  }

  if (!watchAccess && !isGuideOrEvent) {
    return (
      <div className="detail-jumbotron__featured-action-buttons">
        <ButtonSignUp
          text={staticText.getIn(['signUpToWatch'])}
          buttonClass={'detail-jumbotron__featured-button detail-jumbotron__featured-watch-now'}
          type={BUTTON_SIGN_UP_TYPE_BUTTON}
          onClick={() => handleSignUpClicked(props)}
          scrollToTop
        />
      </div>
    )
  }

  return (
    <div className="detail-jumbotron__featured-action-buttons">
      <WatchV2
        iconType={ICON_TYPES.PLAY}
        upstreamContext={jumbotronUpstreamContext}
        buttonType={TYPES.ICON_PRIMARY}
        className="detail-jumbotron__featured-button detail-jumbotron__featured-watch-now"
        text={staticText.get('watchNow')}
        url={watchPath}
        watchAccess={watchAccess}
        isFree={isFreeAllowed}
        onClickWatch={onWatchNowClick}
      />
    </div>
  )
}

/**
 * Detail Access Buttons
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Boolean} props.isSeriesDetailPage - Is the current page a series page
 * @param {Boolean} props.isEpisodeDetailPage - Is the current page an episode page
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isEvent - Is series event
 * @param {Boolean} props.isGuideDetailPage - is a Guide page
 * @param {Boolean} props.hasLiveAccess - If the user has a Gaia+ subscription
 * @returns {JSX.Element} Watch button
 */
function DetailAccessButtons (props) {
  const {
    detailData,
    isSeriesDetailPage,
    staticText,
    isEvent,
    isGuideDetailPage,
    detailLocationType,
    hasLiveAccess,
    hasEntitlement,
    auth,
    onWatchNowClick,
  } = props
  const contentType = detailData.get('contentType')
  const featuredContent = getFeaturedContent({ data: detailData })
  const nodeData = isSeriesDetailPage ? featuredContent : detailData
  const nodeType = getNodeType({ data: detailData })

  const feature = getFeatureMedia({ data: detailData, contentType })
  if (!Map.isMap(feature) || feature.size === 0) {
    return null
  }

  const isGuideOrEvent = isEvent || isGuideDetailPage
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFeatureAllowed = featureAllowedWithUserSubscription({ data: nodeData })
  const isFeatureAvailableToUser = watchAccess || isFeatureAllowed
  const isGaiaPlusOnly = isGaiaPlusFeature(feature)
  const isCountryAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'], true)

  const showUpgradeButton = !hasLiveAccess && isGaiaPlusOnly && isGuideOrEvent && !watchAccess
  const showNotAvailableMessage = !watchAccess && !isCountryAllowed
  const showSignUpButton = !isFeatureAvailableToUser
    && isCountryAllowed
    && !hasEntitlement
    && !showUpgradeButton

  const renderWatchButton = () => {
    const watchPath = nodeData.get('path', '')
    const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])
    const watchClass = showUpgradeButton
      ? 'detail-jumbotron__featured-button-watch'
      : 'detail-jumbotron__featured-button detail-jumbotron__featured-watch-now'

    const upstreamContext = buildJumbotronUpstreamContextGQL({
      auth,
      nodeData,
      parentNodeData: detailData,
      nodeType,
      detailLocationType,
    })

    return <WatchV2
      upstreamContext={upstreamContext}
      className={watchClass}
      text={staticText.get('watchNow')}
      url={watchPath}
      watchAccess={watchAccess}
      isFree={isFreeAllowed}
      iconType={ICON_TYPES.PLAY}
      buttonType={TYPES.ICON_PRIMARY}
      onClickWatch={onWatchNowClick}
    />
  }

  const renderSignUpButton = () => (
    <ButtonSignUp
      text={staticText.getIn(['signUpToWatch'])}
      buttonClass="detail-jumbotron__featured-button"
      type={BUTTON_SIGN_UP_TYPE_BUTTON}
      scrollToTop
      onClick={() => handleSignUpClicked(props)}
    />
  )

  const renderUpgradeButton = () => (
    <Button
      className="detail-jumbotron__featured-button-upgrade"
      url={URL_GAIA_PLUS}
      type={TYPES.LABELED}
      label={staticText.getIn(['watchFullSeries'])}
    >
      {hasEntitlement ? staticText.getIn(['upgrade']) : staticText.getIn(['signUp'])}
    </Button>
  )

  const renderNotAvailableMessage = () => (
    <div className="detail-jumbotron__featured-unavailable-text">
      {staticText.get('notAvailableInArea')}
    </div>
  )

  return (
    <div className="detail-jumbotron__featured-action-buttons">
      {watchAccess && renderWatchButton()}
      {showSignUpButton && renderSignUpButton()}
      {showUpgradeButton && renderUpgradeButton()}
      {showNotAvailableMessage && renderNotAvailableMessage()}
    </div>
  )
}

/**
 * The Detail Jumbotron for GraphQL data
 * @param {Object} props - The props
 * @param props.auth - The authorization
 * @param props.location - The window location
 * @param props.detailData - The content data
 * @param props.isEvent - If this is an event detail page
 * @param props.isGuideDetailPage If this is a guide detail page
 * @param props.isGuideAndNotStarted - If this is a guide and the user hasn't started it yet
 * @param props.isSeriesDetailPageNoEpisodes - If this is a series page and there are no episodes
 * @param props.staticText
 * @returns {JSX.Element} - The detail page jumbotron for graphql data
 */
function DetailJumbotronGraphQL (props) {
  const {
    auth,
    location,
    detailData,
    isEvent,
    isGuideDetailPage,
    isGuideAndNotStarted,
    isSeriesDetailPageNoEpisodes,
    isSeriesDetailPage,
    isEpisodeDetailPage,
    staticText,
  } = props

  const {
    title,
    teaser,
    url,
  } = getData({ data: detailData })
  const { pathname, query } = location
  const parsedUrl = parseUrl(pathname)
  const currentPath = formatUrl({
    ...parsedUrl,
    search: stringifyQuery(query),
  })
  const id = detailData.get('contentId') || 0
  const nodeType = getNodeType({ data: detailData })
  const dispatch = useDispatch()
  let detailLocationType = TYPE_CONTENT_VIDEO

  // Winback Flow:Update the user_is_winback attribute for the winback flow
  useEffect(() => {
    const renderWinbackButton = shouldShowWinbackButtons(props)
    dispatch(updateOptimizelyFeatureUser({
      page_has_winback_ctas: renderWinbackButton,
    }))
  }, [detailData])

  if (isSeriesDetailPage) {
    detailLocationType = TYPE_CONTENT_SERIES
  }

  if (isEpisodeDetailPage) {
    detailLocationType = TYPE_CONTENT_EPISODE
  }

  if (isEvent) {
    detailLocationType = SUBTYPE_CONTENT_EVENT
  }

  if (isGuideDetailPage) {
    detailLocationType = SUBTYPE_CONTENT_GUIDE
  }

  // if the series is published, but has no episodes yet (marketing uses this scenario)
  if (isSeriesDetailPageNoEpisodes) {
    const seriesHost = detailData.get('host')
    const seriesHostLabel = isYogaOrMeditation(detailData.get('contentSubtype'))
      ? staticText.get('teacher')
      : staticText.get('host')
    const seriesPreview = Map.isMap(detailData.get('previewMedia')) ? detailData.getIn(['previewMedia', 'mediaId'], -1) : -1

    return (
      <div className="detail-jumbotron">
        <JumbotronBackground {...props} />
        <div className="detail-jumbotron__content detail-jumbotron__content--no-episodes">
          {
            isEvent || isGuideDetailPage
              ? <GuideEventFlair {...props} />
              : <GaiaOriginal {...props} />
          }
          <SeriesLogoOrTitle {...props} />
          <p className="detail-jumbotron__featured-teaser">
            <Link
              to={`${currentPath}#tabs`}
              className="detail-jumbotron__featured-teaser-link"
            >
              {truncate(teaser, 200)}
            </Link>
          </p>
          {seriesHost && (
            <div className="detail-jumbotron__series-host">
              {seriesHostLabel}: {seriesHost}
            </div>
          )}
          <Tags {...props} />
          {
            seriesPreview && seriesPreview !== -1
              ? <Button
                  url={`${detailData.get('path')}?fullplayer=preview`}
                  type={TYPES.PRIMARY}
                  className="detail-jumbotron__watch-trailer"
                >
                  {staticText.get('playTrailer')}
                </Button>
              : null
          }
        </div>
        {id > 0 && (
          <ButtonAdmin
            id={id}
            auth={auth}
            align={BUTTON_ADMIN_ALIGN_RIGHT_BOTTOM}
            type={nodeType}
          />
        )}
      </div>
    )
  }

  return (
    <div className="detail-jumbotron">
      <JumbotronBackground {...props} />
      <div className="detail-jumbotron__content">
        {
          isEvent || isGuideDetailPage
            ? <GuideEventFlair {...props} />
            : <GaiaOriginal {...props} />
        }
        <SeriesLogoOrTitle {...props} />
        {!isGuideDetailPage && (
          <WatchLabel {...props} />
        )}
        {!isGuideAndNotStarted && (
          <H2
            as={HEADING_TYPES.H6}
            className="detail-jumbotron__featured-title"
          >
            <Link
              to={url}
              className="detail-jumbotron__featured-title-link"
            >
              {title}
            </Link>
          </H2>
        )}
        <p className="detail-jumbotron__featured-teaser">
          <Link
            to={`${currentPath}#tabs`}
            className="detail-jumbotron__featured-teaser-link"
          >
            {truncate(teaser, 200)}
          </Link>
        </p>
        {
        isGuideDetailPage
          ? <FeaturedGuideDetails {...props} />
          : <FeaturedDetails {...props} />
        }
        {
          !isGuideDetailPage
            ? <Tags {...props} detailLocationType={detailLocationType} />
            : null
        }
        <>
          <OptimizelyFeature
            flag={WINBACK_FLAG}
            variation="control"
            isDefault={true}
            anon={false}
            noWrapper
          >
            {
              guidePolicyGaiaPlusUpgrade
                ? <DetailAccessButtons {...props} detailLocationType={detailLocationType} />
                : <WatchNow {...props} detailLocationType={detailLocationType} />
            }
          </OptimizelyFeature>
          <OptimizelyFeature
            flag={WINBACK_FLAG}
            variation={WINBACK_VARIATION}
            noWrapper
          >
            <DetailWinbackButtons {...props} />
          </OptimizelyFeature>
        </>
      </div>
      {id > 0 && (
        <ButtonAdmin
          id={id}
          auth={auth}
          align={BUTTON_ADMIN_ALIGN_RIGHT_BOTTOM}
          type={nodeType}
        />
      )}
    </div>
  )
}

DetailJumbotronGraphQL.propTypes = {
  location: PropTypes.object.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  detailData: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  isVideoDetailPage: PropTypes.bool,
  isEpisodeDetailPage: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  isEvent: PropTypes.bool,
  featureIsGaiaPlus: PropTypes.bool,
  isGuideDetailPage: PropTypes.bool,
  isSeriesDetailPageNoEpisodes: PropTypes.bool,
  hasLiveAccess: PropTypes.bool,
  isGuideAndNotStarted: PropTypes.bool,
  userLanguage: PropTypes.string,
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
        user,
        userAccount,
        paytrack,
        resolver,
      } = state
      const { detailData = Map() } = props
      const contentType = detailData.get('contentType')
      const contentSubtype = detailData.get('contentSubtype')
      const seriesContentSubtype = detailData.get('seriesContentSubtype')
      const displayType = detailData.get('displayType')
      const data = detailData
      const subscriptions = auth.get('subscriptions')
      const hasLiveAccess = hasLiveAccessEntitlement(subscriptions)
      const userLanguage = getPrimary(user.getIn(['data', 'language']))
      const isEpisodeDetailPage = isEpisode({ contentType, data })
      const isEvent = isEpisodeDetailPage
        ? isSubtypeEvent({ contentSubtype: seriesContentSubtype })
        : isSubtypeEvent({ contentSubtype })
      const isLoggedIn = getAuthIsLoggedIn(auth)
      const hasEntitlement = isEntitled(subscriptions)

      // Winback Flow
      const isWinbackFlowActive = shouldShowWinbackCheckout(auth, user, userAccount, paytrack)

      return {
        auth,
        resolver,
        staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
        isVideoDetailPage: isVideo({ contentType, data }),
        isEpisodeDetailPage: isEpisode({ contentType, data }),
        isSeriesDetailPage: isSeries({ contentType }),
        isEvent,
        isGuideDetailPage: isGuide({ contentSubtype, displayType }),
        isSeriesDetailPageNoEpisodes: isSeriesNoEpisodes({ contentType, data }),
        isGuideAndNotStarted: isGuideNotStartedYet({ contentSubtype, displayType, data }),
        featureIsGaiaPlus: isGaiaPlusFeature(detailData.get('featureMedia')),
        hasLiveAccess,
        userLanguage,
        isLoggedIn,
        hasEntitlement,
        isWinbackFlowActive,
      }
    },
  ),
)(DetailJumbotronGraphQL)
